import React, { useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "../../components/Payments/CheckoutForm";
import InvoiceForm from "../../components/Invoices/InvoiceForm";
import API from "../../services/api";
import Modal from "../../components/Facilities/Modal";
import { t } from "@lingui/macro";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  { locale: "es" }
);

const DEFAULT_TEST_RECEIVER = {
  name: "Foreign Test Client Ltd",
  rfc: "XEXX010101000",
  cfdiUse: "S01",
  fiscalRegime: "616",
  taxZipCode: "76130",
};

const TicketSummary = ({ ticketData, onBack }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [receiver, setReceiver] = useState(DEFAULT_TEST_RECEIVER);

  const paymentData = {
    amount: ticketData.total * 100,
    currency: "mxn",
    country: "MX",
    label: t`Total`,
    payerName: ticketData.email,
    payerEmail: ticketData.email,
  };

  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        const response = await API.post(
          "/api/payments/public/create-payment-intent",
          { amount: paymentData.amount }
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error(t`Error fetching client secret:`, error);
        setErrorMessage(t`Payment initialization failed.`);
      }
    };

    if (!clientSecret) {
      fetchClientSecret();
    }
  }, [clientSecret, paymentData.amount]);

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      const ticketOrderRequest = {
        email: ticketData.email,
        isGroup: ticketData.isGroup,
        invoiceRequired: wantsInvoice,
        invoiceReceiver: wantsInvoice ? receiver : null,
        paymentMethod: "stripe",
        total: ticketData.total,
        tickets: ticketData.tickets.map((ticket) => ({
          priceId: ticket.priceId,
          priceCategory: ticket.priceCategory,
          quantity: ticket.quantity,
        })),
        paymentId: paymentIntentId,
      };

      const response = await API.post(
        "/api/tickets/public/order",
        ticketOrderRequest
      );

      if (response.data?.invoiceRequestId) {
        localStorage.setItem("invoiceRequestId", response.data.invoiceRequestId);
      }

      setSuccessMessage(
        wantsInvoice
          ? t`Tickets have been sent. Your invoice is being prepared.`
          : t`Your ticket order was successfully processed. The tickets have been sent to your email (${ticketData.email}).`
      );
      setIsSuccess(true);

    } catch (error) {
      console.error(t`Error submitting order:`, error);
      setErrorMessage(
        error.response?.data?.errorMessage ||
          t`An error occurred. Please try again.`
      );
    }
  };

  return (
    <div className="ticket-summary">
      <div className="summary-details">
        <h2>{t`Order Summary`}</h2>
        <p>{t`Email`}: {ticketData.email}</p>
        <p>
          {t`Is Group Ticket`}: {ticketData.isGroup ? t`Yes` : t`No`}
        </p>
        <p>{t`Total`}: {ticketData.total} MXN</p>

        <h3>{t`Tickets`}:</h3>
        <ul>
          {ticketData.tickets.map((ticket, index) => (
            <li key={index}>
              {ticket.priceCategory}: {ticket.quantity}
            </li>
          ))}
        </ul>

        <div style={{ marginTop: "1rem" }}>
          <label>
            <input
              type="checkbox"
              checked={wantsInvoice}
              onChange={(e) => setWantsInvoice(e.target.checked)}
            />{" "}
            {t`Invoice`}
          </label>
        </div>

        {wantsInvoice && (
          <>
            <p style={{ color: "orange", marginTop: "0.5rem" }}>
              {t`Test data - change to Your data if you need an invoice`}
            </p>

            <InvoiceForm receiver={receiver} setReceiver={setReceiver} />
          </>
        )}
      </div>

      <div className="modal-actions">
        <button className="add-new-btn" onClick={onBack}>
          {t`Back to Ticket Form`}
        </button>
      </div>

      {clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm
            paymentData={paymentData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={(errorMessage) => {
              alert(t`Payment error:` + " " + errorMessage);
            }}
          />
        </Elements>
      )}

      {(successMessage || errorMessage) && (
        <Modal
          message={successMessage || errorMessage}
          onClose={() => {
            setSuccessMessage("");
            setErrorMessage("");
            onBack();
          }}
          isSuccess={isSuccess}
        />
      )}
    </div>
  );
};

export default TicketSummary;
